import { registerFlow } from '../../../core/flow-register';
import FlowHeader from '../../../components/FlowHeader/FlowHeader';
import FooterSite from '../../../components/FooterSite/FooterSite';
import BaseLayout from '../../../components/BaseLayout/BaseLayout';
import InternalAuthenticator from '../../../components/BaseLayout/InternalAuthenticator';
import SummitRewards from '../../../components/BaseLayout/SummitRewards';
import AddressValidationModal from '../../../components/AddressValidationModal/AddressValidationModal';
import CredentialsValidationModal from '../../../components/CredentialsValidationModal/CredentialsValidationModal';
import FONASUMFTaxableUgmaUtmaScreen from '../../../screens/FONASUMFTaxableUGMAUTMA/FONASUMFTaxableUgmaUtmaScreen';
import { FlowNode } from '../../../core/types/flow';
import { contactTextFn, updateFONASUAnalytics } from '../../shared/utils';

import { finish, openModal } from '../../../core/actions';
import {
  isValidSSN,
  isUniqueSSN,
  isValidDob,
  usernameValidation,
  isUsernameAlreadyTaken,
  isUsernameCreationFailed,
  isTempPasswordCreationFailed,
  chosenAddressValidation,
  meetsMinorAgeValidation,
  meetsMinimumInvestment
} from '../../shared/testers';
import { makeLoadTaxableData } from './loaders';
import { createFONASUApplication } from '../shared/loaders';

const nodes: Array<FlowNode> = [
  {
    route: 'fonasuMFTaxableUgmaUtma',
    screen: FONASUMFTaxableUgmaUtmaScreen,
    load: makeLoadTaxableData,
    commit: createFONASUApplication,
    form: [
      { name: 'firstName', required: true },
      { name: 'lastName', required: true },
      { name: 'middleName' },
      { name: 'dob', required: true, validation: isValidDob },
      { name: 'citizenship', required: true },
      { name: 'ssn', required: true, validation: isValidSSN },
      {
        name: 'amount',
        required: true,
        validation: meetsMinimumInvestment
      },
      { name: 'residentialStreet1', required: true },
      { name: 'residentialStreet2' },
      { name: 'residentialZip', required: true },
      { name: 'residentialCity', required: true },
      { name: 'residentialState', required: true },
      { name: 'mailingStreet1', required: true },
      { name: 'mailingStreet2' },
      { name: 'mailingZip', required: true },
      { name: 'mailingCity', required: true },
      { name: 'mailingState', required: true },
      { name: 'primaryEmailAddress', required: true },
      { name: 'phone1', required: true },
      { name: 'phone2' },
      { name: 'phone3' },
      { name: 'mailingAddressDifferent', type: 'checkbox' },
      { name: 'useResidentialAddress', required: true },
      { name: 'useMailingAddress', required: true },
      { name: 'minorFirstName', required: true },
      { name: 'minorMiddleName' },
      { name: 'minorLastName', required: true },
      { name: 'minorDob', required: true, validation: meetsMinorAgeValidation },
      { name: 'minorCitizenship', required: true },
      {
        name: 'minorSsn',
        required: true,
        validation: [isValidSSN, isUniqueSSN]
      },
      { name: 'minorSameAddress', type: 'checkbox' },
      { name: 'minorResidentialStreet1', required: true },
      { name: 'minorResidentialStreet2' },
      { name: 'minorResidentialZip', required: true },
      { name: 'minorResidentialCity', required: true },
      { name: 'minorResidentialState', required: true },
      { name: 'minorGoverningState', required: true },
      {
        name: 'username',
        validation: usernameValidation
      },
      {
        name: 'password'
      }
    ],
    next: {
      cases: [
        [chosenAddressValidation, openModal(AddressValidationModal)],
        [isUsernameAlreadyTaken, openModal(CredentialsValidationModal)],
        [isTempPasswordCreationFailed, openModal(CredentialsValidationModal)],
        [isUsernameCreationFailed, openModal(CredentialsValidationModal)]
      ],
      default: finish()
    },
    flowProps: {
      analytics: {
        pageName: 'FONASUTaxableUGMAUTMA'
      }
    }
  }
];

export default registerFlow({
  namespace: 'front-office/mutual-fund/taxable/ugmautma',
  nodes,
  start: 'fonasuMFTaxableUgmaUtma',
  pageDecorators: [BaseLayout, InternalAuthenticator, SummitRewards],
  onLoad: updateFONASUAnalytics,
  flowProps: {
    getContactText: contactTextFn(),
    header: FlowHeader,
    footer: FooterSite
  }
});
